import { useState, useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Validation rule types
 */
export interface ValidationRule {
  /** Validation function - returns true if valid */
  validate: (value: unknown, formValues?: Record<string, unknown>) => boolean;
  /** Error message to display when validation fails */
  message: string;
}

export interface FieldConfig {
  /** Initial value for the field */
  initialValue?: unknown;
  /** Array of validation rules */
  rules?: ValidationRule[];
  /** Whether to validate on blur */
  validateOnBlur?: boolean;
  /** Whether to validate on change */
  validateOnChange?: boolean;
}

export interface FieldState {
  value: unknown;
  error: string | null;
  touched: boolean;
  dirty: boolean;
}

export interface FormState {
  isValid: boolean;
  isSubmitting: boolean;
  isDirty: boolean;
  submitCount: number;
}

export interface UseFormValidationReturn<T extends Record<string, FieldConfig>> {
  /** Current values of all fields */
  values: { [K in keyof T]: unknown };
  /** Errors for each field */
  errors: { [K in keyof T]: string | null };
  /** Whether each field has been touched */
  touched: { [K in keyof T]: boolean };
  /** Form-level state */
  formState: FormState;
  /** Set a field's value */
  setValue: (field: keyof T, value: unknown) => void;
  /** Set multiple values at once */
  setValues: (values: Partial<{ [K in keyof T]: unknown }>) => void;
  /** Mark a field as touched */
  setTouched: (field: keyof T, touched?: boolean) => void;
  /** Set a field's error manually */
  setError: (field: keyof T, error: string | null) => void;
  /** Validate a single field */
  validateField: (field: keyof T) => boolean;
  /** Validate all fields */
  validateForm: () => boolean;
  /** Reset form to initial state */
  reset: () => void;
  /** Reset a single field */
  resetField: (field: keyof T) => void;
  /** Get props for an input element */
  getFieldProps: (field: keyof T) => {
    value: unknown;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    onBlur: () => void;
    name: string;
    'aria-invalid': boolean;
    'aria-describedby': string | undefined;
  };
  /** Handle form submission */
  handleSubmit: (onSubmit: (values: { [K in keyof T]: unknown }) => Promise<void> | void) => (e: React.FormEvent) => void;
}

/**
 * Form validation hook with real-time validation and accessibility support
 */
export function useFormValidation<T extends Record<string, FieldConfig>>(
  config: T
): UseFormValidationReturn<T> {
  type FieldNames = keyof T;

  // Initialize field states
  const initialFieldStates = useMemo(() => {
    const states: Record<string, FieldState> = {};
    for (const [name, fieldConfig] of Object.entries(config)) {
      states[name] = {
        value: fieldConfig.initialValue ?? '',
        error: null,
        touched: false,
        dirty: false,
      };
    }
    return states as { [K in FieldNames]: FieldState };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [fieldStates, setFieldStates] = useState<{ [K in FieldNames]: FieldState }>(initialFieldStates);
  const [formState, setFormState] = useState<FormState>({
    isValid: true,
    isSubmitting: false,
    isDirty: false,
    submitCount: 0,
  });

  // Get current values object
  const values = useMemo(() => {
    const vals: Record<string, unknown> = {};
    for (const [name, state] of Object.entries(fieldStates)) {
      vals[name] = state.value;
    }
    return vals as { [K in FieldNames]: unknown };
  }, [fieldStates]);

  // Get errors object
  const errors = useMemo(() => {
    const errs: Record<string, string | null> = {};
    for (const [name, state] of Object.entries(fieldStates)) {
      errs[name] = state.error;
    }
    return errs as { [K in FieldNames]: string | null };
  }, [fieldStates]);

  // Get touched object
  const touched = useMemo(() => {
    const t: Record<string, boolean> = {};
    for (const [name, state] of Object.entries(fieldStates)) {
      t[name] = state.touched;
    }
    return t as { [K in FieldNames]: boolean };
  }, [fieldStates]);

  // Validate a single field
  const validateField = useCallback((field: FieldNames): boolean => {
    const fieldConfig = config[field];
    const value = fieldStates[field].value;
    const rules = fieldConfig.rules || [];

    for (const rule of rules) {
      if (!rule.validate(value, values)) {
        setFieldStates(prev => ({
          ...prev,
          [field]: { ...prev[field], error: rule.message },
        }));
        return false;
      }
    }

    setFieldStates(prev => ({
      ...prev,
      [field]: { ...prev[field], error: null },
    }));
    return true;
  }, [config, fieldStates, values]);

  // Validate all fields
  const validateForm = useCallback((): boolean => {
    let isValid = true;
    const newStates = { ...fieldStates };

    for (const [name, fieldConfig] of Object.entries(config)) {
      const value = fieldStates[name as FieldNames].value;
      const rules = fieldConfig.rules || [];

      for (const rule of rules) {
        if (!rule.validate(value, values)) {
          newStates[name as FieldNames] = {
            ...newStates[name as FieldNames],
            error: rule.message,
            touched: true,
          };
          isValid = false;
          break;
        } else {
          newStates[name as FieldNames] = {
            ...newStates[name as FieldNames],
            error: null,
          };
        }
      }
    }

    setFieldStates(newStates as { [K in FieldNames]: FieldState });
    setFormState(prev => ({ ...prev, isValid }));
    return isValid;
  }, [config, fieldStates, values]);

  // Keep latest validateField in a ref so deferred validation reads the
  // post-update closure, not the one captured when setValue was called.
  const validateFieldRef = useRef(validateField);
  useEffect(() => {
    validateFieldRef.current = validateField;
  }, [validateField]);

  // Set a field value
  const setValue = useCallback((field: FieldNames, value: unknown) => {
    setFieldStates(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        dirty: true,
      },
    }));
    setFormState(prev => ({ ...prev, isDirty: true }));

    // Validate on change if configured
    const fieldConfig = config[field];
    if (fieldConfig.validateOnChange) {
      setTimeout(() => validateFieldRef.current(field), 0);
    }
  }, [config]);

  // Set multiple values
  const setValues = useCallback((newValues: Partial<{ [K in FieldNames]: unknown }>) => {
    setFieldStates(prev => {
      const updated = { ...prev };
      for (const [name, value] of Object.entries(newValues)) {
        if (name in updated) {
          updated[name as FieldNames] = {
            ...updated[name as FieldNames],
            value,
            dirty: true,
          };
        }
      }
      return updated;
    });
    setFormState(prev => ({ ...prev, isDirty: true }));
  }, []);

  // Set touched state
  const setTouched = useCallback((field: FieldNames, isTouched = true) => {
    setFieldStates(prev => ({
      ...prev,
      [field]: { ...prev[field], touched: isTouched },
    }));

    // Validate on blur if configured
    const fieldConfig = config[field];
    if (fieldConfig.validateOnBlur !== false && isTouched) {
      validateField(field);
    }
  }, [config, validateField]);

  // Set error manually
  const setError = useCallback((field: FieldNames, error: string | null) => {
    setFieldStates(prev => ({
      ...prev,
      [field]: { ...prev[field], error },
    }));
  }, []);

  // Reset form
  const reset = useCallback(() => {
    setFieldStates(initialFieldStates);
    setFormState({
      isValid: true,
      isSubmitting: false,
      isDirty: false,
      submitCount: 0,
    });
  }, [initialFieldStates]);

  // Reset single field
  const resetField = useCallback((field: FieldNames) => {
    setFieldStates(prev => ({
      ...prev,
      [field]: initialFieldStates[field],
    }));
  }, [initialFieldStates]);

  // Get field props for input binding
  const getFieldProps = useCallback((field: FieldNames) => {
    const state = fieldStates[field];
    const hasError = state.touched && state.error !== null;

    return {
      value: state.value as string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setValue(field, e.target.value);
      },
      onBlur: () => setTouched(field),
      name: String(field),
      'aria-invalid': hasError,
      'aria-describedby': hasError ? `${String(field)}-error` : undefined,
    };
  }, [fieldStates, setValue, setTouched]);

  // Handle form submission
  const handleSubmit = useCallback(
    (onSubmit: (values: { [K in FieldNames]: unknown }) => Promise<void> | void) =>
      async (e: React.FormEvent) => {
        e.preventDefault();

        setFormState(prev => ({
          ...prev,
          submitCount: prev.submitCount + 1,
          isSubmitting: true,
        }));

        const isValid = validateForm();

        if (isValid) {
          try {
            await onSubmit(values);
          } catch (error) {
            console.error('Form submission error:', error); // intentional: consumer's onSubmit handles its own errors
          }
        }

        setFormState(prev => ({ ...prev, isSubmitting: false }));
      },
    [validateForm, values]
  );

  return {
    values,
    errors,
    touched,
    formState,
    setValue,
    setValues,
    setTouched,
    setError,
    validateField,
    validateForm,
    reset,
    resetField,
    getFieldProps,
    handleSubmit,
  };
}

/**
 * Common validation rules
 */
export const validators = {
  required: (message = 'This field is required'): ValidationRule => ({
    validate: (value) => {
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined;
    },
    message,
  }),

  email: (message = 'Please enter a valid email address'): ValidationRule => ({
    validate: (value) => {
      if (!value || typeof value !== 'string') return true;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    },
    message,
  }),

  minLength: (length: number, message?: string): ValidationRule => ({
    validate: (value) => {
      if (!value || typeof value !== 'string') return true;
      return value.length >= length;
    },
    message: message || `Must be at least ${length} characters`,
  }),

  maxLength: (length: number, message?: string): ValidationRule => ({
    validate: (value) => {
      if (!value || typeof value !== 'string') return true;
      return value.length <= length;
    },
    message: message || `Must be no more than ${length} characters`,
  }),

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    validate: (value) => {
      if (!value || typeof value !== 'string') return true;
      return regex.test(value);
    },
    message,
  }),

  min: (minValue: number, message?: string): ValidationRule => ({
    validate: (value) => {
      if (value === null || value === undefined || value === '') return true;
      return Number(value) >= minValue;
    },
    message: message || `Must be at least ${minValue}`,
  }),

  max: (maxValue: number, message?: string): ValidationRule => ({
    validate: (value) => {
      if (value === null || value === undefined || value === '') return true;
      return Number(value) <= maxValue;
    },
    message: message || `Must be no more than ${maxValue}`,
  }),

  match: (fieldName: string, message = 'Fields must match'): ValidationRule => ({
    validate: (value, formValues) => {
      if (!formValues) return true;
      return value === formValues[fieldName];
    },
    message,
  }),

  custom: (validateFn: (value: unknown) => boolean, message: string): ValidationRule => ({
    validate: validateFn,
    message,
  }),
};
